import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHead, Loading, Pill, BrandDot, Empty } from "@/components/common";
import { toast } from "sonner";
import { Search, X } from "lucide-react";

export default function Customers() {
  const { selectedBrand, brands } = useApp();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [adj, setAdj] = useState({ brand_id: "", points: 0, reason: "" });

  const load = () => {
    const p = new URLSearchParams();
    if (selectedBrand !== "ALL") p.set("brand", selectedBrand);
    if (search) p.set("search", search);
    api.get(`/customers?${p}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [selectedBrand, search]);

  const open = async (c) => {
    const { data } = await api.get(`/customers/${c.id}`);
    setDetail(data);
    setAdj({ brand_id: c.brands?.[0] || "brownsugar", points: 0, reason: "" });
  };

  const adjust = async () => {
    try {
      await api.post(`/customers/${detail.customer.id}/adjust`, { ...adj, points: parseInt(adj.points) });
      toast.success("Adjustment posted to ledger");
      open(detail.customer); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHead overline="Customer Directory" title="Customers & Passbook" desc="One identity per program. Append-only ledger is the source of truth; available points exclude reserved amounts." />
      <div className="relative max-w-sm mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="customer-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or phone…" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm" />
      </div>
      {!data ? <Loading /> : data.items.length === 0 ? <Empty /> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider"><tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Phone</th><th className="text-left px-4 py-3">Brands</th><th className="text-left px-4 py-3">City</th><th className="text-right px-4 py-3">Total Points</th><th></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{c.name}{c.demo && <span className="ml-2 text-[10px] text-amber-600">demo</span>}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.phone}</td>
                  <td className="px-4 py-3">{(c.brands || []).map((b) => <span key={b}><BrandDot id={b} /></span>)}</td>
                  <td className="px-4 py-3 text-slate-500">{c.city || "—"}</td>
                  <td className="px-4 py-3 text-right font-bold text-violet-600">{c.total_points}</td>
                  <td className="px-4 py-3 text-right"><button data-testid={`customer-view-${c.id}`} onClick={() => open(c)} className="text-violet-600 text-xs font-semibold hover:underline">Passbook</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={() => setDetail(null)}>
          <div className="w-full max-w-lg bg-white h-full p-6 overflow-y-auto fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div><h3 className="text-lg font-bold font-head">{detail.customer.name}</h3><div className="text-xs font-mono text-slate-400">{detail.customer.phone}</div></div>
              <button onClick={() => setDetail(null)}><X size={18} className="text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {Object.entries(detail.balances).map(([b, p]) => (
                <div key={b} className="bg-slate-50 rounded-lg p-2 text-center"><div className="text-[10px] text-slate-400 uppercase"><BrandDot id={b} />{b}</div><div className="font-bold">{p}</div></div>
              ))}
            </div>
            <div className="bg-violet-50 border border-violet-100 rounded-lg p-3 mb-4">
              <div className="text-xs font-semibold text-violet-800 mb-2">Manual adjustment (audited, requires reason)</div>
              <div className="flex gap-2 mb-2">
                <select value={adj.brand_id} onChange={(e) => setAdj({ ...adj, brand_id: e.target.value })} className="px-2 py-1.5 rounded border border-slate-200 text-sm">
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <input type="number" value={adj.points} onChange={(e) => setAdj({ ...adj, points: e.target.value })} className="w-20 px-2 py-1.5 rounded border border-slate-200 text-sm" />
              </div>
              <input value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder="Reason…" className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm mb-2" />
              <button data-testid="adjust-points-btn" onClick={adjust} className="text-sm font-semibold text-violet-700 bg-white border border-violet-200 px-3 py-1.5 rounded-lg">Post adjustment</button>
            </div>
            <h4 className="font-semibold text-slate-700 mb-2 text-sm">Passbook ledger</h4>
            <div className="space-y-1.5">
              {detail.ledger.map((l) => (
                <div key={l.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2">
                  <div><div className="font-medium text-slate-700">{l.reason}</div><div className="text-[11px] text-slate-400"><BrandDot id={l.brand_id} />{l.type} · {l.at?.slice(0, 10)}</div></div>
                  <div className={`font-bold ${l.points >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{l.points >= 0 ? "+" : ""}{l.points}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
